/** Shared market symbol formatting and spot-pair lookup. */
import { marketNameSerializer } from "./format";
import { ellipseString } from "./string";

export function getMarketCurrencies(marketName: string) {
  const currencies = marketName.split("/");
  return {
    primaryCurrency: currencies[0] ?? "",
    secondaryCurrency: currencies[1] ?? "",
  };
}

/** Extracts the base symbol from a market name or a legacy dash slug. */
export function getMarketBaseSymbol(marketName: string): string {
  const marketPair = marketName.includes("/")
    ? marketName
    : marketNameSerializer.deserialize(marketName);
  return getMarketCurrencies(marketPair).primaryCurrency;
}

export function ellipseMarketName(marketName: string, delimiter = "/") {
  const { primaryCurrency, secondaryCurrency } = getMarketCurrencies(marketName);

  const ellipsedPrimaryCurrency = ellipseCurrency(primaryCurrency);

  return `${ellipsedPrimaryCurrency}${delimiter}${secondaryCurrency}`;
}

// Used to ellipse the market name in the UI, we can move to /constants if we use it elsewhere
const LONG_MARKET_NAME_MAX_LENGTH = 8;

export const ellipseCurrency = (currency: string) => {
  return ellipseString(currency, LONG_MARKET_NAME_MAX_LENGTH);
};

/**
 * Resolve "which spot market trades this asset" from the symbol a holdings row displays — the join
 * a balance row needs to link to its market.
 *
 * Keyed on symbol rather than asset address because the server derives one from the other: a spot
 * position's `asset_symbol` is `market_name.split('/')[0]` of the market the asset is the base of
 * (`extract_base_symbol`, in both the HTTP and the WS overview builder), and a holding with no such
 * market is dropped from the response entirely. Matching a symbol back to a market name therefore
 * inverts a known function rather than guessing, and the markets list — already loaded for routing
 * — is the only input needed. Joining on `base_asset_addr` instead would mean fetching the spot
 * asset contexts purely to recover a string we were handed.
 *
 * The one case this is weaker than an address join: a symbol the row took from on-chain FA metadata
 * (its fallback for an asset the API couldn't label, e.g. one held only as CBS collateral) is not
 * market-derived, so a collateral asset whose on-chain symbol collides with some market's base
 * would link to that market. Admin allowlisting is what stands between users and the collision, and
 * landing on the wrong book is recoverable.
 *
 * Only base symbols resolve: a quote asset (USDC today) is held against every market rather than
 * traded by one, so there is no single market it points at. When a symbol is the base of several
 * markets, the one quoted in `preferredQuoteSymbol` wins and ties fall to list order, so the target
 * can't shift with the server's market ordering once a second quote asset ships.
 */
export function makeSpotMarketByBaseSymbol<T extends { market_name: string }>(
  spotMarkets: readonly T[],
  preferredQuoteSymbol: string,
): (symbol: string) => T | undefined {
  const preferredQuote = preferredQuoteSymbol.toUpperCase();
  const byBaseSymbol = new Map<string, { market: T; isPreferredQuote: boolean }>();

  for (const market of spotMarkets) {
    const { primaryCurrency, secondaryCurrency } = getMarketCurrencies(market.market_name);
    if (!primaryCurrency) continue;

    const baseKey = primaryCurrency.toUpperCase();
    const isPreferredQuote = secondaryCurrency.toUpperCase() === preferredQuote;
    const incumbent = byBaseSymbol.get(baseKey);
    if (incumbent && (incumbent.isPreferredQuote || !isPreferredQuote)) continue;

    byBaseSymbol.set(baseKey, { market, isPreferredQuote });
  }

  return (symbol: string) => (symbol ? byBaseSymbol.get(symbol.toUpperCase())?.market : undefined);
}
